import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useAdminAccess from '../../../context/useAdminAccess';
import ItAppSection from './ItAppSection';
import ItAppDrawer from './ItAppDrawer';
import { COMPANY_ORDER } from './itAppRules';
import { listItApps } from './itAppsApi';
// ut-drawer + backdrop rules live with the users tab; reused here as-is.
import '../usersTab/usersTab.css';
import './itApps.css';

const NEW = 'new';

function matches(app, q) {
  if (!q) return true;
  return [app.name, app.key, app.it_owner, app.tool_owner, app.partner_name]
    .some((v) => typeof v === 'string' && v.toLowerCase().includes(q));
}

/** /admin/it_apps: the register of apps IT runs, grouped by company (spec §4.2). */
export default function ItAppsTab() {
  const { can } = useAdminAccess();
  const canManage = can('it_apps.manage');
  const [state, setState] = useState({ status: 'loading', apps: [] });
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null); // app id, NEW, or null

  const load = useCallback(() => {
    setState((s) => ({ ...s, status: 'loading' }));
    listItApps()
      .then((apps) => setState({ status: 'ready', apps }))
      // A failed read is an error state, never an empty register.
      .catch(() => setState({ status: 'error', apps: [] }));
  }, []);

  useEffect(() => { load(); }, [load]);

  const { apps } = state;
  const q = search.trim().toLowerCase();
  const filtering = !!q;
  const visible = useMemo(() => apps.filter((a) => matches(a, q)), [apps, q]);

  const onSaved = useCallback((saved, { close }) => {
    if (!saved) return;
    setState((s) => {
      const exists = s.apps.some((a) => a.id === saved.id);
      const next = exists ? s.apps.map((a) => (a.id === saved.id ? saved : a)) : [...s.apps, saved];
      return { ...s, apps: next };
    });
    setSelected(close ? null : saved.id);
  }, []);
  const closeDrawer = useCallback(() => setSelected(null), []);

  const selectedApp = selected && selected !== NEW ? apps.find((a) => a.id === selected) : null;
  const addButton = canManage && (
    <button type="button" className="btn btn-primary ita-btn" onClick={() => setSelected(NEW)}>Add app</button>
  );

  let body;
  if (state.status === 'loading') {
    // Section-shaped placeholders, so the page doesn't jump when the register lands.
    body = (
      <div className="ita-skeleton" aria-busy="true" aria-label="Loading IT apps">
        {COMPANY_ORDER.map((c) => (
          <div key={c} className="ita-skeleton-section">
            <div className="ita-skeleton-bar ita-skeleton-bar--head" />
            <div className="ita-skeleton-bar" />
            <div className="ita-skeleton-bar" />
          </div>
        ))}
      </div>
    );
  } else if (state.status === 'error') {
    body = (
      <div className="ita-state" role="alert">
        <p className="ita-state-title">Couldn't load IT apps.</p>
        <p className="ita-state-text">Check your connection, then try again.</p>
        <button type="button" className="btn btn-secondary ita-btn" onClick={load}>Try again</button>
      </div>
    );
  } else if (apps.length === 0) {
    body = (
      <div className="ita-state">
        <p className="ita-state-title">No IT apps yet.</p>
        <p className="ita-state-text">
          {canManage
            ? 'Add the first app IT runs to start the register.'
            : 'Apps IT runs will show here once someone adds them.'}
        </p>
        {addButton}
      </div>
    );
  } else {
    const sections = COMPANY_ORDER
      .map((company) => ({
        company,
        all: apps.filter((a) => a.company === company),
        rows: visible.filter((a) => a.company === company),
      }))
      .filter((s) => !filtering || s.rows.length > 0);
    body = (
      <>
        {filtering && visible.length === 0 && (
          <div className="ita-state ita-state--quiet">
            <p className="ita-state-title">No apps match your search.</p>
            <p className="ita-state-text">Try an app name, key or owner.</p>
          </div>
        )}
        {sections.map((s) => (
          <ItAppSection key={s.company} company={s.company} allApps={s.all} apps={s.rows} onOpen={(a) => setSelected(a.id)} />
        ))}
      </>
    );
  }

  return (
    <div className="ita">
      {state.status === 'ready' && apps.length > 0 && (
        <div className="ita-toolbar">
          <input
            type="search"
            className="search-input ita-search"
            placeholder="Search by app, key or owner"
            aria-label="Search IT apps"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {/* Always mounted so screen readers hear the count change as the user types. */}
          <p className="ita-count" role="status">
            {filtering ? `${visible.length} of ${apps.length} ${apps.length === 1 ? 'app' : 'apps'}` : ''}
          </p>
          {addButton}
        </div>
      )}
      {body}
      {selected && (selected === NEW || selectedApp) && (
        <ItAppDrawer
          key={selected}
          app={selectedApp}
          canManage={canManage}
          onClose={closeDrawer}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}
